import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiSend, FiUser, FiMail, FiMessageSquare } from "react-icons/fi";
import axios from "axios";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    honey: "", // honeypot field (should stay empty)
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      newErrors.name = "Please enter at least 2 characters.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      newErrors.message = "Message should be at least 10 characters.";
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Block bots: if honeypot filled, silently succeed to avoid spammers probing
    if (formData.honey) {
      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "", honey: "" });
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }

    const validation = validate();
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(
        "https://getform.io/f/312fccc0-f13c-489d-b160-4888b8029c7b",
        formData,
        { headers: { Accept: "application/json" } }
      );
      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "", honey: "" });
    } catch (error) {
      console.error("Form submission error:", error?.response?.data || error.message);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  return (
    <div
      name="contact"
      id="contact"
      className="w-full min-h-screen pt-24 md:pt-32 transition-colors duration-300
                 bg-gradient-to-b from-gray-50 via-white to-gray-100
                 dark:from-gray-900 dark:via-black dark:to-gray-800"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 md:py-20 flex flex-col justify-center">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mb-12 md:mb-16 text-center"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 inline-block text-gray-900 dark:text-white">
            Get In Touch
            <span className="block mt-2 h-1 w-28 mx-auto bg-gradient-to-r from-cyan-500 to-blue-600 rounded"></span>
          </h2>
          <p className="text-gray-700 dark:text-gray-300 text-base sm:text-lg max-w-2xl mx-auto">
            Have a project in mind or want to discuss opportunities? Send me a message below.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.45 }}
          className="flex justify-center"
        >
          <form
            id="contact-form"
            onSubmit={handleSubmit}
            noValidate
            className="w-full md:w-2/3 lg:w-1/2 space-y-5"
          >
            {/* Honeypot (hidden) */}
            <input
              type="text"
              name="honey"
              value={formData.honey}
              onChange={handleChange}
              className="hidden"
              tabIndex="-1"
              autoComplete="off"
            />

            {/* Name */}
            <div className="relative">
              <label htmlFor="name" className="sr-only">Your Name</label>
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiUser className="text-gray-400" />
              </div>
              <input
                type="text"
                name="name"
                id="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
                className={`w-full pl-10 p-3 rounded-lg outline-none transition-all
                            bg-white border border-gray-300 text-gray-900
                            focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                            dark:bg-gray-800 dark:border-gray-700 dark:text-white
                            ${errors.name ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
              />
              {errors.name && (
                <p id="name-error" className="mt-2 text-sm text-red-500">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="relative">
              <label htmlFor="email" className="sr-only">Your Email</label>
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiMail className="text-gray-400" />
              </div>
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                required
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-error" : undefined}
                className={`w-full pl-10 p-3 rounded-lg outline-none transition-all
                            bg-white border border-gray-300 text-gray-900
                            focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                            dark:bg-gray-800 dark:border-gray-700 dark:text-white
                            ${errors.email ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
              />
              {errors.email && (
                <p id="email-error" className="mt-2 text-sm text-red-500">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Message */}
            <div className="relative">
              <label htmlFor="message" className="sr-only">Your Message</label>
              <div className="absolute top-3 left-3 pointer-events-none">
                <FiMessageSquare className="text-gray-400" />
              </div>
              <textarea
                name="message"
                id="message"
                value={formData.message}
                onChange={handleChange}
                rows="6"
                placeholder="Your Message"
                required
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? "message-error" : undefined}
                className={`w-full pl-10 p-3 rounded-lg outline-none transition-all resize-y
                            bg-white border border-gray-300 text-gray-900
                            focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
                            dark:bg-gray-800 dark:border-gray-700 dark:text-white
                            ${errors.message ? "border-red-500 focus:ring-red-500 focus:border-red-500" : ""}`}
              />
              {errors.message && (
                <p id="message-error" className="mt-2 text-sm text-red-500">
                  {errors.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <motion.button
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              type="submit"
              disabled={isSubmitting}
              className={`w-full flex items-center justify-center gap-2 text-white font-medium py-3 px-6 rounded-lg
                          bg-gradient-to-r from-cyan-600 to-blue-600
                          hover:from-cyan-500 hover:to-blue-500
                          shadow-lg hover:shadow-cyan-500/20 transition-all
                          disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <>
                  <span className="inline-block h-5 w-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <FiSend /> Send Message
                </>
              )}
            </motion.button>

            {/* Status */}
            <div aria-live="polite">
              {submitStatus === "success" && (
                <div className="mt-3 p-4 text-green-700 bg-green-100 border border-green-200 rounded-lg dark:bg-green-900/30 dark:text-green-300 dark:border-green-800">
                  Message sent successfully! I'll get back to you soon.
                </div>
              )}
              {submitStatus === "error" && (
                <div className="mt-3 p-4 text-red-700 bg-red-100 border border-red-200 rounded-lg dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
                  There was a problem sending your message. Please check the form and try again.
                </div>
              )}
            </div>
          </form>
        </motion.div>

        {/* Direct email */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="mt-14 text-center"
        >
          <p className="text-gray-700 dark:text-gray-300">
            Alternatively, reach me directly at{" "}
            <a
              href="mailto:yssnmed@gmail.com"
              className="text-cyan-700 dark:text-cyan-400 hover:underline"
            >
              yssnmed@gmail.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;